<div class="multi-invoice-selection">
  <!-- Loading State -->
  <div class="loading-state" *ngIf="isLoading">
    <mat-spinner diameter="40"></mat-spinner>
    <p>Loading the customer's balance...</p>
  </div>

  @if (!isLoading) {
    <!-- Balance header -->
    @if (balances; as b) {
      <div class="balance-summary" aria-label="Customer balance">
        <div class="balance-summary__tile balance-summary__tile--current">
          <span class="balance-summary__label">Current balance</span>
          <span class="balance-summary__value">{{ b.currentBalance | currency }}</span>
        </div>
        <div class="balance-summary__tile">
          <span class="balance-summary__label">Invoice balance</span>
          <span class="balance-summary__value">{{ b.invoiceBalance.amount | currency }}</span>
          <span class="balance-summary__count">{{ b.invoiceBalance.count }} open</span>
        </div>
        <div class="balance-summary__tile" matTooltip="Finance charges, service fees and card fees with no invoice behind them" matTooltipPosition="above">
          <span class="balance-summary__label">Non-invoice balance</span>
          <span class="balance-summary__value">{{ b.nonInvoiceBalance.amount | currency }}</span>
          <span class="balance-summary__count">{{ b.nonInvoiceBalance.count }} open</span>
        </div>
        <div class="balance-summary__tile" [class.balance-summary__tile--alert]="b.overdue.amount > 0">
          <span class="balance-summary__label">Overdue</span>
          <span class="balance-summary__value">{{ b.overdue.amount | currency }}</span>
          <span class="balance-summary__count">{{ b.overdue.count }} past due</span>
        </div>
        <div class="balance-summary__tile" [class.balance-summary__tile--warn]="b.dueToday.amount > 0">
          <span class="balance-summary__label">Due today</span>
          <span class="balance-summary__value">{{ b.dueToday.amount | currency }}</span>
          <span class="balance-summary__count">{{ b.dueToday.count }} due</span>
        </div>
        <div class="balance-summary__tile balance-summary__tile--credit">
          <span class="balance-summary__label">Credits on file</span>
          <span class="balance-summary__value">{{ b.creditsOnFile.amount | currency }}</span>
          <span class="balance-summary__count">{{ b.creditsOnFile.count }} open</span>
        </div>
        <!--
          Only shown once there is something to show: signed, so it is absent for every profile
          without the feature and for every customer who has never had one posted. A seventh tile
          reading $0.00 on every customer everywhere would be noise.
        -->
        @if (b.balanceEntries; as be) {
          @if (be.count > 0) {
            <div
              class="balance-summary__tile balance-summary__tile--balance-entry"
              [class.balance-summary__tile--credit]="be.amount < 0"
              matTooltip="Fees and credits posted straight to the balance, with no invoice or credit memo behind them"
              matTooltipPosition="above"
            >
              <span class="balance-summary__label">Posted to balance</span>
              <span class="balance-summary__value">{{ be.amount | currency }}</span>
              <span class="balance-summary__count">{{ be.count }} {{ be.count === 1 ? 'entry' : 'entries' }}</span>
            </div>
          }
        }
      </div>
    }

    <!-- One payment amount, spread over the selected invoices oldest due first. Not in an
         Advanced FlashPay send, where each ticked invoice is requested at its full balance. -->
    @if (!sendMode) {
      <div class="payment-total">
        <div class="payment-total__text">
          <strong>Payment amount</strong>
          <span>
            Fills the selected invoices oldest due first@if (onAccountEnabled) {; the rest goes to the account}.
            Leave empty to set an amount per invoice.
          </span>
        </div>
        <div class="payment-total__input">
          <mat-form-field appearance="outline" class="payment-amount-field" floatLabel="always">
            <mat-label>Payment amount</mat-label>
            <span matTextPrefix>$&nbsp;</span>
            <input
              matInput
              type="text"
              aria-label="Payment amount to allocate oldest invoice first"
              [ngModel]="paymentTotal"
              (ngModelChange)="setPaymentTotal($event)"
              currencyMask
              [options]="{ prefix: '', thousands: ',', decimal: '.', precision: 2, min: 0, allowNegative: false, nullable: true }"
            />
          </mat-form-field>
        </div>
      </div>
    }

    <!-- Apply to account (profile's allowPaymentToAccount) -->
    @if (onAccountEnabled) {
      <div class="on-account">
        <div class="on-account__text">
          <strong>Apply to account</strong>
          <span>
            @if (isAmountMode) {
              What the payment amount leaves once the selected invoices are paid.
            } @else {
              An amount paid onto the customer's account, not to any invoice.
            }
          </span>
        </div>
        <div class="on-account__input">
          <mat-form-field appearance="outline" class="payment-amount-field">
            <mat-label>{{ isAmountMode ? 'Remainder to account' : 'Amount to account' }}</mat-label>
            <span matTextPrefix>$&nbsp;</span>
            <input
              matInput
              type="text"
              aria-label="Amount to apply to the account"
              [readonly]="isAmountMode"
              [ngModel]="onAccountAmount"
              (ngModelChange)="updateOnAccountAmount($event)"
              currencyMask
              [options]="{ prefix: '', thousands: ',', decimal: '.', precision: 2, min: 0, allowNegative: false }"
            />
          </mat-form-field>
          @if (onAccountMax !== null) {
            <span class="max-amount-label" [class.over]="onAccountAmount > onAccountMax">Max: {{ onAccountMax | currency }}</span>
          }
        </div>
      </div>
    }

    <div class="balance-columns" [class.balance-columns--single]="!creditsEnabled">
      <!-- Invoices on file, oldest due first -->
      <section class="balance-column" aria-label="Invoices on file">
        <div class="selection-header">
          <div class="selection-info">
            <span class="column-title">Invoices on file</span>
            <span class="selection-count" *ngIf="selectedInvoices.size > 0">
              <strong>{{ selectedInvoices.size }}</strong> of <strong>{{ payableInvoices.length }}</strong> selected
            </span>
            <span class="selection-total" *ngIf="selectedInvoices.size > 0">
              ({{ getTotalSelectedAmount() | currency }})
            </span>
          </div>

          <div class="selection-actions">
            <button mat-button type="button" (click)="clearSelection()" *ngIf="selectedInvoices.size > 0" class="clear-button">
              <mat-icon>clear</mat-icon>
              Clear Selection
            </button>
            <button
              mat-raised-button
              type="button"
              color="primary"
              (click)="selectAll()"
              [disabled]="payableInvoices.length === 0 || selectedInvoices.size === payableInvoices.length"
              class="select-all-button"
            >
              <mat-icon>select_all</mat-icon>
              Select All
            </button>
          </div>
        </div>

        @if (invoices.length === 0) {
          <div class="no-invoices-state">
            <mat-icon>receipt_long</mat-icon>
            <p>No unpaid invoices found for this customer.</p>
          </div>
        } @else {
          <div class="invoice-list">
            <div
              *ngFor="let invoice of invoices"
              class="invoice-item"
              [class.selected]="isInvoiceSelected(invoice)"
            >
              <div class="invoice-header">
                <!-- Every listed invoice is payable: `loadInvoices` excludes NotPayable, which
                     FTE would refuse anyway. They stay visible on the Invoices tab. -->
                <mat-checkbox
                  [checked]="isInvoiceSelected(invoice)"
                  (change)="toggleInvoiceSelection(invoice, $event.checked)"
                  class="invoice-checkbox"
                  [attr.aria-label]="'Pay ' + invoice.invoiceNumber"
                >
                </mat-checkbox>

                <div class="invoice-info">
                  <h3 class="invoice-number">
                    {{ invoice.invoiceNumber }}
                    @if (typeLabel(invoice); as label) {
                      <span class="doc-type-chip">{{ label }}</span>
                    }
                  </h3>
                  <p class="invoice-date">
                    <mat-icon>calendar_today</mat-icon>
                    Date: {{ invoice.invoiceDate | date : 'mediumDate' }}
                  </p>
                  <p class="invoice-due-date" [class.overdue]="invoice.status === 'overdue'">
                    <mat-icon>event</mat-icon>
                    Due: {{ invoice.dueDate | date : 'mediumDate' }}
                  </p>
                </div>

                <div class="invoice-amounts">
                  <div class="amount-row">
                    <span class="label">Outstanding:</span>
                    <span class="value">{{ getOutstandingBalance(invoice) | currency }}</span>
                  </div>
                  @if (isInvoiceSelected(invoice) && getCreditAmount(invoice) > 0) {
                    <div class="amount-row amount-row--credit">
                      <span class="label">Credit applied:</span>
                      <span class="value">−{{ getCreditAmount(invoice) | currency }}</span>
                    </div>
                    <div class="amount-row">
                      <span class="label">To charge:</span>
                      <span class="value">{{ getCashAmount(invoice) | currency }}</span>
                    </div>
                  }
                </div>
              </div>

              <!-- Payment Amount Input (only shown when selected) -->
              <div class="invoice-payment" *ngIf="isInvoiceSelected(invoice) && !sendMode">
                <div class="payment-input-wrapper">
                  <mat-form-field appearance="outline" class="payment-amount-field">
                    <mat-label>Payment Amount</mat-label>
                    <span matTextPrefix>$&nbsp;</span>
                    <!-- min 0, not 0.01: with a 0.01 floor the mask put the cent back when the
                         field was cleared, and since it types right-to-left every new amount
                         started with that 1 (typing 555 gave 15.55). A $0 amount on a ticked
                         invoice is refused by currentValidationError instead. -->
                    <input
                      matInput
                      type="text"
                      [ngModel]="getPaymentAmount(invoice)"
                      (ngModelChange)="updatePaymentAmount(invoice, $event)"
                      currencyMask
                      [options]="{
                        prefix: '',
                        thousands: ',',
                        decimal: '.',
                        precision: 2,
                        max: getOutstandingBalance(invoice),
                        min: 0
                      }"
                    />
                  </mat-form-field>
                  @if (isUnreached(invoice)) {
                    <span class="max-amount-label not-reached">Not reached by the payment amount</span>
                  } @else {
                    <span class="max-amount-label">Max: {{ getOutstandingBalance(invoice) | currency }}</span>
                  }
                </div>
              </div>

              <!-- Status Badge -->
              <div class="invoice-status">
                <span class="status-badge" [class]="invoice.status">
                  {{ invoice.status | uppercase }}
                </span>
              </div>
            </div>
          </div>
        }
      </section>

      <!-- Credit on file (profile's enableApplyCreditMemo) -->
      @if (creditsEnabled) {
        <section class="balance-column balance-column--credits" aria-label="Credit on file">
          <div class="selection-header">
            <div class="selection-info">
              <span class="column-title">Credit on file</span>
              @if (getCreditsAppliedTotal() > 0) {
                <span class="selection-total selection-total--credit">applying {{ getCreditsAppliedTotal() | currency }}</span>
              }
            </div>
          </div>

          @if (credits.length === 0) {
            <div class="no-invoices-state no-invoices-state--compact">
              <mat-icon>savings</mat-icon>
              <p>No credit on file.</p>
            </div>
          } @else {
            <p class="credit-hint">
              Click a credit to apply it. It is spread over the selected invoices, largest first.
              @if (requireFullCredit) {
                A credit must be applied in full, so the invoices selected have to cover all of it.
              }
            </p>
            <div class="credit-list">
              @for (credit of credits; track credit.creditInvoiceRef) {
                <button
                  type="button"
                  class="credit-item"
                  [class.credit-item--applied]="isCreditApplied(credit)"
                  [attr.aria-pressed]="isCreditApplied(credit)"
                  [attr.aria-label]="
                    (isCreditApplied(credit) ? 'Stop applying ' : 'Apply ') + creditRefLabel(credit) + ', ' + (creditAvailable(credit) | currency) + ' available'
                  "
                  (click)="toggleCredit(credit)"
                >
                  <span class="credit-item__top">
                    <span class="credit-item__number">{{ creditRefLabel(credit) }}</span>
                    <mat-icon class="credit-item__check" aria-hidden="true">
                      {{ isCreditApplied(credit) ? 'check_circle' : 'add_circle_outline' }}
                    </mat-icon>
                  </span>
                  <span class="credit-item__meta">
                    <span class="doc-type-chip" [class.doc-type-chip--balance]="isBalanceCredit(credit)">{{ creditLabel(credit) }}</span>
                    @if (credit.date) {
                      <span>{{ credit.date | date : 'mediumDate' }}</span>
                    }
                  </span>
                  <!--
                    A balance credit carries the reason somebody typed when they granted it; a
                    credit document has none. It is the only thing identifying it, since its ref
                    is a ledger id no ERP would recognise.
                  -->
                  @if (credit.reason) {
                    <span class="credit-item__reason">{{ credit.reason }}</span>
                  }
                  <span class="credit-item__amounts">
                    <span>
                      Available
                      <strong>{{ creditAvailable(credit) | currency }}</strong>
                    </span>
                    @if (isCreditApplied(credit)) {
                      <span class="credit-item__used">
                        Applying <strong>{{ creditUsedAmount(credit) | currency }}</strong>
                        @if (creditUsedAmount(credit) === 0) {
                          — select an invoice
                        }
                      </span>
                      @if (creditShortfallFor(credit) > 0) {
                        <span class="credit-item__short">
                          {{ creditShortfallFor(credit) | currency }} would be left over — select more invoices
                        </span>
                      }
                    }
                  </span>
                </button>
              }
            </div>
          }
        </section>
      }
    </div>

    <!-- Total Section -->
    <div class="total-section" *ngIf="selectedInvoices.size > 0 || onAccountAmount > 0">
      <div class="total-row" *ngIf="selectedInvoices.size > 0">
        <span class="total-label">Selected invoices ({{ selectedInvoices.size }}):</span>
        <span class="total-count">{{ getTotalSelectedAmount() | currency }}</span>
      </div>

      <div class="total-row total-row--credit" *ngIf="getCreditsAppliedTotal() > 0">
        <span class="total-label">Credits applied:</span>
        <span class="total-count">−{{ getCreditsAppliedTotal() | currency }}</span>
      </div>

      <div class="total-row" *ngIf="onAccountAmount > 0">
        <span class="total-label">Applied to account:</span>
        <span class="total-count">{{ onAccountAmount | currency }}</span>
      </div>

      <div class="total-row" *ngIf="!sendMode">
        <span class="total-label">Subtotal to charge:</span>
        <span class="total-count">{{ getCashSubtotal() | currency }}</span>
      </div>

      <!-- Capital Cost Toggle -->
      <div class="capital-cost-section" *ngIf="!sendMode && enableCostOfCapital && getCashSubtotal() > 0">
        <div class="capital-cost-toggle-row">
          <div class="capital-cost-info">
            <span class="capital-cost-label">{{ getCapitalCostLabel() }}</span>
            <span class="capital-cost-amount" *ngIf="capitalCostEnabled">{{ capitalCostAmount | currency }}</span>
          </div>
          <mat-slide-toggle
            [(ngModel)]="capitalCostEnabled"
            (change)="toggleCapitalCost($event.checked)"
            color="primary"
            class="capital-cost-toggle"
          >
          </mat-slide-toggle>
        </div>
      </div>

      <div class="total-row total-amount-row">
        <span class="total-label">{{ sendMode ? 'Total to request:' : 'Total to charge:' }}</span>
        <span class="total-amount">{{ getTotalAmount() | currency }}</span>
      </div>
      @if (creditCoversAll) {
        <p class="credit-covers-all">
          <mat-icon aria-hidden="true">savings</mat-icon>
          Credit covers this payment — nothing will be charged.
        </p>
      }
    </div>

    <!-- Validation Messages -->
    @if (selectionProblem; as problem) {
      <div class="validation-messages" role="alert">
        <mat-icon>error_outline</mat-icon>
        <p>{{ problem }}</p>
      </div>
    }
    <div class="validation-messages" *ngIf="selectedInvoices.size === 0 && !(onAccountAmount > 0)">
      <mat-icon>info</mat-icon>
      <p>
        {{
          sendMode
            ? 'Select the invoices to send. The customer can pay any or all of them from the email.'
            : onAccountEnabled
            ? 'Select at least one invoice, or enter an amount to apply to the account.'
            : 'Please select at least one invoice to proceed.'
        }}
      </p>
    </div>
  }
</div>
